<script lang="ts" setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";

import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import { GridComponent, LegendComponent } from "echarts/components";
import { SVGRenderer } from "echarts/renderers";

// Config
import { getConfig, getThemeColor } from "@/features/Charts/config/echarts";
import { readChartTokens, type ChartTokens } from "@/features/Charts/config/chartTokens";
import { useThemeChange } from "@/features/Charts/composables/useThemeChange";
import { getBarEndRadius, getBarRadii } from "@/features/Charts/utils/barBorderRadius";
import { getRowHover, type RowHoverEntry } from "@/features/Charts/utils/rowHover";
import { getValueLabelAnchors } from "@/features/Charts/utils/valueLabelAnchors";
import { getValueAxisOverrides } from "@/features/Charts/utils/valueAxisOverrides";
import { prefersReducedMotion, seriesMotion } from "@/features/Charts/utils/chartMotion";
import { shouldHandOverRows, zeroedSeries, type DrawnSeries } from "@/features/Charts/utils/rowsOutHandover";
import { isPaddedCategory, padCategories, padValues } from "@/features/Charts/utils/padRows";
import type { ChartTooltipEventType } from "@/features/Charts/types/ChartType";
import format from "@/services/Utils/format";

// Props
import { horizontalBarChartProps } from "../props/echartProps";

// Register the required components
echarts.use([BarChart, LegendComponent, GridComponent, SVGRenderer]);

// The first series takes the brand blue so dashboard cards match the deep-dive's total bar; later series fall back
// to the theme's solid families — a value-tinted gradient faded small bars to near-invisible pale blue.
const barColor = (seriesIndex: number, tokens: ChartTokens) =>
  seriesIndex === 0 ? tokens.primaryBar : getThemeColor(seriesIndex);

// Mirrors config/echarts.ts HorizontalBarChart series.barMaxWidth — the bar's thickness cap.
const BAR_MAX_THICKNESS_PX = 18;
// Horizontal acceptance beyond the bar's ends, keeping a near-zero (sub-pixel wide) bar targetable.
// Deliberately NOT applied vertically: a vertical grace would swallow the gap between bars.
const ROW_HOVER_GRACE_PX = 4;

const props = defineProps({
  ...horizontalBarChartProps,
});

const categories = ref<string[]>([]);
// Rows the data actually has, which is fewer than `categories` once minRows padding is in play.
let rowCount = 0;

const chartRef = ref<HTMLElement | null>(null);
let chart: echarts.EChartsType | null = null;
let resizeObserver: ResizeObserver | null = null;
// Container size at the previous draw, so a redraw only resizes when the box really changed.
const lastDrawSize = { w: 0, h: 0 };
// The option's inputs at the previous draw, so an unchanged redraw does not replay the animation.
let lastDrawSignature: string | null = null;
// What the previous draw put on screen, so a navigation can animate those rows out first. Item ids
// matter: ECharts diffs by id, so the rows-out draw must name the SAME items to be animated.
let lastDrawnCategories: string[] = [];
let lastDrawnSeries: DrawnSeries[] = [];
let rowsOutTimer: ReturnType<typeof setTimeout> | null = null;

const config = ref();

// Row-hover state: `hoveredSegment` lets a genuine segment hover win over the row fallback; `rowHoverData` refreshes
// on every draw (re-registering the listener instead would stack duplicate listeners per update).
let hoveredSegment = false;
let rowHoverData: { labels: string[]; entries: RowHoverEntry[][]; series: { name: string; color?: string }[] } = {
  labels: [],
  entries: [],
  series: [],
};
let hoveredRowIndex: number | null = null;

// The row's composition at a category, with the same names/colours the bars use — shared by both
// hover kinds so a caller can render one tooltip layout for either.
const rowBreakdownAt = (categoryIndex: number) =>
  rowHoverData.entries.map((entries, seriesIndex) => ({
    name: rowHoverData.series[seriesIndex]?.name ?? "",
    value: entries[categoryIndex]?.value ?? 0,
    color: rowHoverData.series[seriesIndex]?.color,
  }));

// Only dismisses a tooltip this fallback opened — a segment hover owns its own leave event.
const clearRowHover = () => {
  if (hoveredRowIndex === null) return;
  hoveredRowIndex = null;
  props.onLeave?.();
};

const preamble = () => {
  // Get config
  config.value = getConfig("HorizontalBarChart", props.settings, props.data);

  // Register theme
  echarts.registerTheme(`${config.value.name}Theme`, config.value.theme);

  // Get categories (only if not already set), padded out to settings.minRows when the caller wants a
  // stable row pitch — every index at or past `rowCount` is an invisible filler slot.
  const labels = Object.values(props.data)[0].entries.map((entry) => entry.label);
  rowCount = labels.length;
  categories.value = padCategories(labels, props.settings.minRows);
};

// Must match chartMotion's rows-out duration: ECharts takes an update's duration from the option
// already in place, so passing one with the rows-out draw itself would be ignored.
const ROWS_OUT_MS = 150;

const shouldAnimateRowsOut = () =>
  shouldHandOverRows({
    settings: props.settings,
    reducedMotion: prefersReducedMotion(),
    drawnCategories: lastDrawnCategories,
    drawnSeries: lastDrawnSeries,
    nextCategories: categories.value,
  });

const initChart = (drawOptions?: { afterRowsOut?: boolean }) => {
  if (!chartRef.value) return;

  // Redraw only when the option's inputs really changed — `settings` is a fresh object literal each
  // render, and replaceMerge would otherwise replay the grow-in animation. Exempt after rows-out.
  if (!drawOptions?.afterRowsOut) {
    const drawSignature = JSON.stringify([props.data, props.unit, props.settings, props.eventMetadata()]);
    if (chart && drawSignature === lastDrawSignature) return;
    lastDrawSignature = drawSignature;
  }

  // Create the echarts instance once and reuse it on subsequent updates —
  // re-initializing per data/unit change leaks the previous instance and its listeners
  if (!chart) {
    chart = echarts.init(chartRef.value, `${config.value.name}Theme`);
    // The size ECharts just measured is the baseline the resize check below compares against.
    // Seeded from zero, the first draw resized itself mid-animation and snapped into place.
    lastDrawSize.w = chartRef.value.clientWidth;
    lastDrawSize.h = chartRef.value.clientHeight;

    // Wire chart events once, on the persistent instance
    chart.on("mouseover", (params) => {
      if (params.seriesType !== "bar") return;
      // A real segment hover outranks the row fallback below: this event can name the stage.
      hoveredSegment = true;
      hoveredRowIndex = null;
      if (!props.onHover) return;
      // Segment events carry the row's composition too, so a caller can render ONE tooltip layout for
      // both hover kinds — `seriesName` stays available for one that wants the stage line instead.
      props.onHover(Object.assign(params, { rowBreakdown: rowBreakdownAt(params.dataIndex) }) as ChartTooltipEventType);
    });

    chart.on("mouseout", (params) => {
      if (params.seriesType !== "bar") return;
      hoveredSegment = false;
      if (props.onLeave) props.onLeave();
    });

    // A segment's hit area is its ink, so a one-pixel stage can't be targeted directly — this fallback
    // maps the pointer to the bar's rectangle (+ grace) and reports the whole row (`seriesName` unset).
    chart.getZr().on("mousemove", (zrEvent) => {
      if (!chart || hoveredSegment) return;

      // Category axis is the yAxis here, spanning -0.5 to count-0.5 across the grid — a pointer outside
      // that range (legend, ticks, unit caption) is rejected rather than snapped to the first/last row.
      const [, rawIndex] = chart.convertFromPixel({ gridIndex: 0 }, [zrEvent.offsetX, zrEvent.offsetY]) as number[];
      if (!Number.isFinite(rawIndex) || rawIndex < -0.5 || rawIndex > rowHoverData.labels.length - 0.5) {
        clearRowHover();
        return;
      }

      const categoryIndex = Math.round(rawIndex);

      // A minRows filler slot holds space and nothing else: it has no bar and no numbers, so there
      // is nothing to say about it.
      if (isPaddedCategory(rowHoverData.labels[categoryIndex] ?? "")) {
        clearRowHover();
        return;
      }

      // Vertically: strictly within the bar's own thickness in pixels (bars are capped by barMaxWidth
      // while the slot grows with the grid) — no grace, or it would re-cover the gap between bars.
      const chartInstance = chart;
      const yAt = (index: number) => (chartInstance.convertToPixel({ gridIndex: 0 }, [0, index]) as number[])[1];
      const rowCount = rowHoverData.labels.length;
      const bandPx = Math.abs(yAt(rowCount - 0.5) - yAt(-0.5)) / rowCount;
      // ECharts' default bar sizing: 80% of the band, capped by the config's barMaxWidth.
      const barPx = Math.min(bandPx * 0.8, BAR_MAX_THICKNESS_PX);
      if (Math.abs(zrEvent.offsetY - yAt(categoryIndex)) > barPx / 2) {
        clearRowHover();
        return;
      }

      const row = getRowHover(rowHoverData.labels, rowHoverData.entries, categoryIndex);
      if (!row) {
        clearRowHover();
        return;
      }

      // Horizontally: within the bar's own extent — for a stack, negative sum to positive; side by
      // side, the union of the series' extents from zero, plus grace so a near-zero bar stays targetable.
      const extent = props.settings.stacked
        ? row.values.reduce(
            (acc, value) => {
              if (value > 0) acc.hi += value;
              else acc.lo += value;
              return acc;
            },
            { lo: 0, hi: 0 }
          )
        : { lo: Math.min(0, ...row.values), hi: Math.max(0, ...row.values) };
      const xAt = (value: number) => (chartInstance.convertToPixel({ gridIndex: 0 }, [value, categoryIndex]) as number[])[0];
      const xLo = xAt(extent.lo);
      const xHi = xAt(extent.hi);
      if (
        zrEvent.offsetX < Math.min(xLo, xHi) - ROW_HOVER_GRACE_PX ||
        zrEvent.offsetX > Math.max(xLo, xHi) + ROW_HOVER_GRACE_PX
      ) {
        clearRowHover();
        return;
      }

      // Re-emitting the same row on every pixel of movement would rebuild the tooltip constantly.
      if (hoveredRowIndex === categoryIndex) return;
      hoveredRowIndex = categoryIndex;

      // Shaped like the ECharts item event callers already handle, minus the per-stage fields — a
      // deliberate partial cast, since ECElementEvent's other internal properties go unread.
      props.onHover?.({
        componentType: "series",
        seriesType: "bar",
        name: row.label,
        value: row.total,
        dataIndex: categoryIndex,
        data: { value: row.total, id: row.id },
        // What the row is made of, so the tooltip can show a breakdown rather than one number.
        rowBreakdown: rowBreakdownAt(categoryIndex),
      } as unknown as ChartTooltipEventType);
    });

    chart.getZr().on("globalout", () => clearRowHover());

    chart.on("click", (params) => {
      if (props.onClick) {
        props.onClick(params);
      }
    });
  }

  // Rows out, then rows in: outgoing bars animate to zero first, and the zeroed data carries no
  // per-bar labels so no row flashes "0.00" on the way out.
  if (!drawOptions?.afterRowsOut && shouldAnimateRowsOut()) {
    if (rowsOutTimer) clearTimeout(rowsOutTimer);
    chart.setOption({ series: zeroedSeries(lastDrawnSeries) });
    rowsOutTimer = setTimeout(() => {
      rowsOutTimer = null;
      initChart({ afterRowsOut: true });
    }, ROWS_OUT_MS);
    return;
  }

  // Read once per draw so a theme flip (which disposes and re-runs this function) always gets a
  // fresh reading instead of a colour baked in at an earlier theme.
  const tokens = readChartTokens();

  // Per-bar total label (opt-in via settings.valueLabel). Precompute each category's total across
  // all series and the grand total so the label can show "<total> (<share>%)" at the bar's end.
  const seriesEntries = Object.entries(props.data);
  const showShare = props.settings.valueLabel === "total";
  const showTotalLabel = showShare || props.settings.valueLabel === "value";
  // Over the real rows only: a padding slot has no value, and counting it as 0 would put a "0.00"
  // label on an empty row and drag the share percentages towards it.
  const realRows = Array.from({ length: rowCount });
  const seriesValues = seriesEntries.map(([, item]) =>
    realRows.map((_, categoryIndex) => item.entries[categoryIndex]?.value ?? 0)
  );
  // Refresh what the grid listener reads, so row hover follows current data without re-wiring — names
  // and colours come along so the tooltip's swatches match the bars.
  rowHoverData = {
    labels: categories.value,
    entries: seriesEntries.map(([, item]) => categories.value.map((_, categoryIndex) => item.entries[categoryIndex])),
    series: seriesEntries.map(([key, item], index) => ({ name: item.name ?? key, color: item.color ?? barColor(index, tokens) })),
  };
  const categoryTotals = showTotalLabel
    ? realRows.map((_, categoryIndex) => seriesValues.reduce((sum, values) => sum + values[categoryIndex], 0))
    : [];
  // Absolute totals: a dataset mixing positive and negative rows has a signed grand total small
  // enough to push a single row's share past 100%. Summing magnitudes keeps every share in 0-100%.
  const grandMagnitude = categoryTotals.reduce((sum, value) => sum + Math.abs(value), 0);
  // The label rides on whichever segment owns the bar's rightmost edge — a fixed series would drop
  // it on top of the bar once that series stacks the other way (a negative segment).
  const labelAnchors = showTotalLabel ? getValueLabelAnchors(seriesValues, rowCount) : [];
  // Corner radii per segment, following the value's sign — the config's fixed radius would round a
  // negative bar's zero-line end instead of its free end, making it read as pointing the wrong way.
  const barRadii = getBarRadii(seriesValues, rowCount, {
    orientation: "horizontal",
    stacked: props.settings.stacked,
  });

  const totalLabel = {
    show: true,
    position: "right",
    // Keep the text clear of the bar's coloured end so it always reads against the chart background.
    distance: 8,
    fontSize: 12, // t-caption – the app's smallest UI text size
    fontWeight: 600,
    color: tokens.text,
    formatter: (params: { dataIndex: number }) => {
      const total = categoryTotals[params.dataIndex] ?? 0;
      if (!showShare) return format.decimalNumber(total);
      // Unsigned: `total` already carries the sign, and a share is a magnitude.
      const share = grandMagnitude ? Math.round((Math.abs(total) / grandMagnitude) * 100) : 0;
      return `${format.decimalNumber(total)} (${share}%)`;
    },
  };

  // Draw the chart
  chart.setOption(
    {
      grid: {
        ...config.value.grid,
      },
      legend: {
        show: props.settings.legend,
      },
      yAxis: {
        ...config.value.yAxis,
        data: categories.value,
        inverse: props.settings.inverse,
        axisLabel: {
          ...config.value.yAxis.axisLabel,
          rotate: props.settings.rotateLabels ? 45 : 0,
          // Vue replaces the whole settings object rather than merging it into the prop default,
          // so fall back to the config's own budget when a caller omits the key.
          width: props.settings.categoryLabelWidth ?? config.value.yAxis.axisLabel.width,
          // A minRows filler slot exists to hold the row pitch, so it must not print anything.
          formatter: (value: string) => (isPaddedCategory(value) ? "" : value),
        },
      },
      xAxis: {
        ...config.value.xAxis,
        name: props.unit,
        // Always spread all three keys, `null` when the caller pins nothing — setOption merges the xAxis,
        // so omitting a key would leave an earlier draw's value in place instead of releasing it.
        ...getValueAxisOverrides(props.settings),
      },
      series: seriesEntries.map(([key, item], index) => ({
        ...config.value.series,
        // A stable identity per series so a redraw UPDATES it instead of replaceMerge destroying and
        // rebuilding it (which skipped the leave animation and blanked the plot for a frame).
        id: `${key}`,
        // ECharts applies barMinHeight per segment, not per bar — in a stack a zero-valued stage would
        // still draw a rect on top of its neighbour. Drop the minimum in a stack so length matches value.
        ...(props.settings.stacked ? { barMinHeight: 0 } : {}),
        realtimeSort: props.settings.realtimeSort,
        // One timing for arriving, leaving and moving bars, so a drilldown reads as one movement.
        ...seriesMotion(props.settings, { reducedMotion: prefersReducedMotion() }),
        name: item.name ?? key,
        itemStyle: {
          ...config.value.series.itemStyle,
          // Honor an explicit per-series color when provided (e.g. a fixed brand color per stage
          // bucket); callers that omit it keep the theme/getColor gradient behavior below.
          ...(item.color ? { color: item.color } : {}),
        },
        // Padded to match the axis: a filler slot is a null, which ECharts draws as nothing.
        data: padValues(
          item.entries.slice(0, rowCount).map((entry, categoryIndex) => ({
            value: entry.value,
            id: entry.id,
            itemStyle: {
              // Per item, not per series: rounding depends on this value's sign and, in a stack, on
              // whether this segment is the outer one on its side of zero — inner segments come back square.
              borderRadius: barRadii[index]?.[categoryIndex] ?? getBarEndRadius(entry.value, "horizontal"),
              ...(props.settings.legend || item.color ? {} : { color: barColor(index, tokens) }),
            },
            // Renders once per bar: only the anchor segment for this row carries the label.
            ...(showTotalLabel && labelAnchors[categoryIndex] === index ? { label: totalLabel } : {}),
            ...props.eventMetadata(),
          })),
          props.settings.minRows
        ),
        stack: props.settings.stacked ? "stack" : undefined,
        cursor: props.onClick ? "pointer" : "default",
      })),
    },
    // Replace the series array wholesale: the default merge is by index, so toggling a stage off
    // (fewer series) would leave the previous stage's bars behind and render the stack wrong.
    { replaceMerge: ["series"] }
  );

  // Resizing right after setOption forces an immediate relayout that lands on the animation's last
  // frame, killing the motion — only resize when the container size actually changed since the last draw.
  if (chartRef.value.clientWidth !== lastDrawSize.w || chartRef.value.clientHeight !== lastDrawSize.h) {
    lastDrawSize.w = chartRef.value.clientWidth;
    lastDrawSize.h = chartRef.value.clientHeight;
    chart.resize();
  }

  // What is on screen now, which is what the next navigation animates out.
  lastDrawnCategories = [...categories.value];
  lastDrawnSeries = seriesEntries.map(([key, item]) => ({
    id: `${key}`,
    itemIds: padValues(
      item.entries.slice(0, rowCount).map((entry) => entry.id),
      props.settings.minRows
    ).map((itemId) => itemId ?? undefined),
  }));
};

const handleResize = async () => {
  await nextTick();
  chart?.resize();
};

// A scroll moves the chart out from under a stationary cursor with no mouse event, stranding the
// tooltip. Capture phase, because the dashboard scrolls an inner container, not the window.
const handleAnyScroll = () => {
  if (!hoveredSegment && hoveredRowIndex === null) return;
  hoveredSegment = false;
  hoveredRowIndex = null;
  props.onLeave?.();
};

onMounted(() => {
  // Initialize the chart if possible
  if (props.data && Object.values(props.data).length > 0) {
    // Preamble
    preamble();

    // Initialize the chart
    initChart();

    // Keep the chart sized to its container and the window
    if (chartRef.value) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(chartRef.value);
    }

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleAnyScroll, { capture: true, passive: true });
  }
});

onUnmounted(() => {
  // Tear down the resize handlers and dispose the chart to avoid leaks
  if (rowsOutTimer) clearTimeout(rowsOutTimer);
  resizeObserver?.disconnect();

  window.removeEventListener("resize", handleResize);
  window.removeEventListener("scroll", handleAnyScroll, { capture: true });

  chart?.dispose();
  chart = null;
});

// `settings` is watched alongside the data: a settings-only change (axis bounds, value labels,
// stacking) must still redraw, or the chart silently keeps the previous option.
watch(
  () => [props.data, props.unit, props.settings],
  () => {
    // Initialize the chart if possible
    if (props.data && Object.values(props.data).length > 0) {
      // Preamble
      preamble();

      // Initialize the chart
      initChart();
    }
  },
  {
    deep: true,
  }
);

// A live dark/light flip bakes new colours in by disposing and redrawing — the registered theme
// (and every literal colour below) is only read at init/draw time, not reactively.
useThemeChange(() => {
  if (!props.data || Object.values(props.data).length === 0) return;
  if (rowsOutTimer) clearTimeout(rowsOutTimer);
  chart?.dispose();
  chart = null;
  preamble();
  initChart();
});
</script>
<template>
  <div
    ref="chartRef"
    class="h-full w-full" />
</template>
